import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { fetchAsync } from "../services/ApiHandler";
import { Account } from "../model/Account";
import { Subscription } from "../model/Subscription";
import { MyPageMenu } from "../parts/MyPageMenu";
import { Inspiration } from "../model/Inspiration";
import { ModelTaste } from "../model/ModelTaste";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { InspirationKeyword } from "../model/InspirationKeyword";
import { Modal } from "@mantine/core";
import toast, { Toaster } from "react-hot-toast";
import { InspirationDialog } from "../parts/InspirationDialog";
import { useDisclosure } from "@mantine/hooks";
import { Helmet } from "react-helmet";
import _debounce from 'lodash/debounce';
import { FooterMenu } from "../components/FooterMenu";


export const InspirationPage: FC = () => {
  const [allSampleList, setAllSampleList] = useState<Inspiration[]>([]);
  const [sampleList, setSampleList] = useState<Inspiration[]>([]);
  const [keywordList, setKeywordList] = useState<InspirationKeyword[]>([]);
  const [keyword, setKeyword] = useState<string>('');

  const [mainTasteList, setMainTasteList] = useState<ModelTaste[]>([]);
  const [sample, setSample] = useState<Inspiration | undefined>(undefined);
  const [index, setIndex] = useState<number>(1);
  const [inspirationDialogOpened, { open: openInspirationDialog, close: closeInspirationDialog }] = useDisclosure(false);
  const [processing, setProcessing] = useState(false);

  // const isLoading = useMemo(() => processing || index > 1, [processing, index]);


  useEffect(() => {
    async function fetchMainTasteList (){
      const list = await fetchAsync<ModelTaste[]>('ModelTaste/GetList');
      setMainTasteList(list);
    };

    async function fetchKeywordList (){
      const list = await fetchAsync<InspirationKeyword[]>('Inspiration/GetKeywordList');
      setKeywordList(list);
    };

    fetchKeywordList();
    fetchMainTasteList();
    fetchList();
  }, [])

  const fetchList = async () => {
    if(processing) return;

    setProcessing(true);
    var params = {
      page: index,
      count: 1000,
    };
    const response = await fetchAsync<Inspiration[]>('Inspiration/GetList', params);

    // response.sort(() => Math.random() - 0.5);
    // var newList = [...allSampleList, ...response];

    setAllSampleList(response);
    setSampleList(response);
    setIndex(1);
    setProcessing(false);
  };

  const visibleList = useMemo(() => {
    const maxItems = Math.min(sampleList.length, index * 50);
    return sampleList.slice(0, maxItems);
  }, [sampleList, index]);

  const debounceFn = useMemo(() => _debounce(() => {
    setIndex(prev => {
      const maxPage = Math.ceil(sampleList.length / 50);
      if (prev >= maxPage) {
        return prev;
      }
      return prev + 1;
    });
  }, 500), [sampleList.length]);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const fullHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 200 >= fullHeight && !processing) {
      debounceFn();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      debounceFn.cancel();
    };
  }, [debounceFn]);

  useEffect(() => {
    onChangeKeyword();
  },[keyword]);

  function onChangeKeyword() {
    if (keyword === '') {
      setSampleList(allSampleList);
      setIndex(1);
      return;
    }

    var list = keywordList.filter((e) => e.keyword.startsWith(keyword));
    
    var newList: Inspiration[] = [];
    for(var sample of allSampleList) {
      var selectedList = list.filter((e) => e.inspirationNumber === sample.inspirationNumber);
      if(selectedList.length === 1) {
        newList.push(sample);
      }
    }

    setSampleList(newList);
    setIndex(1);
  }

  function onSelectSample(sample: Inspiration) {
    setSample(sample);
    openInspirationDialog();
  }

  function buildDialog(){
    return <>
    <Modal size="auto" opened={inspirationDialogOpened} onClose={() => closeInspirationDialog()}>
      <InspirationDialog inspiration={sample} mainTasteList={mainTasteList} />
    </Modal>
    </>;
  }

  return <>
    { sample && buildDialog() }
    <Helmet>
      <title>インスピレーション - Drop AI</title>
      <meta name="description" content="インスピレーションページの豊富な画像コレクションから画像生成のヒントやプロンプトを探そう" />
      <meta property="og:url" content="https://drop4i.com/inspiration" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="インスピレーション - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="インスピレーションページの豊富な画像コレクションから画像生成のヒントやプロンプトを探そう" />
    </Helmet>
    <Toaster />
    <div className="">
      <div className="mt-0 sm:mt-12 m-auto max-w-[1200px] bg-white"> 
        <div className="text-center py-0 sm:py-4">
          <img src="/images/symbol-drop.svg" className="hidden sm:block w-[60px] text-center center" />
          <div className="hidden sm:block mt-4">
            <h2 className="text-[24px] font-bold gradation-text">クリエイティブインスピレーションを得よう</h2>
          </div>
          <div className="hidden sm:block mt-2 mb-8 px-4">
            <span className="text-[16px]">画像コレクションから構成ヒントや参考プロンプトを探そう</span>
          </div>
        </div>

        <div className="min-h-[calc(100vh-76px)] mb-12 pb-16 sm:pb-0">
          <div className="sm:hidden text-left mb-4 px-4 pt-2 flex items-center">
            <img src="/images/icon/icons8-gallery-512.png" className="w-5 h-5 mr-2" alt="インスピレーション" />
            <h2 className="text-lg font-bold">インスピレーションを得る</h2>
          </div>
          { visibleList.length > 0 && (
            <div className="px-4 sm:px-6 mb-6">
              <ResponsiveMasonry columnsCountBreakPoints={{ 360: 2, 768: 3, 1024: 4 }}>
                <Masonry gutter="16px">
                  { visibleList.map((item) => (
                    <div key={item.inspirationNumber} className="overflow-hidden rounded-xl bg-gray-100">
                      <div
                        className="w-full"
                        style={{ aspectRatio: item.imageWidth > 0 && item.imageHeight > 0 ? `${item.imageWidth}/${item.imageHeight}` : '1 / 1' }}
                      >
                        <img
                          onClick={() => onSelectSample(item)}
                          className="w-full h-full object-cover cursor-pointer"
                          loading="lazy"
                          src={`${process.env.REACT_APP_IMAGE_URL}/sample/${item.imageFileName.replace(".jpeg", ".jpeg")}`}
                          alt={item.title || 'Inspiration'}
                        />
                      </div>
                    </div>
                  ))}
                </Masonry>
              </ResponsiveMasonry>
            </div>
          )}
        </div>
      </div>
    </div>
    <FooterMenu />
  </>;
}
  
